const getApiBaseUrl = () => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
      return 'https://api.tiwarisyndicate.com/api';
    }
  }
  return process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
};

const API_BASE_URL = getApiBaseUrl();
const DEFAULT_RETRY_COUNT = 1;
const DEFAULT_RETRY_DELAY_MS = 300;

function buildUrl(path) {
  if (path.startsWith('http')) return path;
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const finalUrl = `${API_BASE_URL}${normalizedPath}`;


  return finalUrl;
}

async function parseJson(res) {
  try {
    return await res.json();
  } catch (err) {
    return null;
  }
}

function sleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function shouldRetry(err, attempt, retryCount) {
  if (attempt >= retryCount) return false;
  if (typeof err.status !== 'number') return true;
  return err.status >= 500 || err.status === 429;
}

function normalizeBody(data) {
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    if (Object.prototype.hasOwnProperty.call(data, 'success')) {
      return data.data;
    }
  }

  return data;
}

function getErrorMessage(data) {
  if (!data) return 'Request failed';
  if (typeof data === 'string') return data;
  if (typeof data.message === 'string' && data.message) return data.message;
  if (typeof data.error === 'string' && data.error) return data.error;
  return 'Request failed';
}

async function request(path, options = {}) {
  const {
    retryCount = DEFAULT_RETRY_COUNT,
    retryDelayMs = DEFAULT_RETRY_DELAY_MS,
    unwrap = false,
    headers,
    body,
    ...fetchOptions
  } = options;

  const requestHeaders = {
    Accept: 'application/json',
    ...(body ? { 'Content-Type': 'application/json' } : {}),
    ...(headers || {}),
  };

  for (let attempt = 0; attempt <= retryCount; attempt += 1) {
    try {
      const res = await fetch(buildUrl(path), {
        credentials: 'include',
        ...fetchOptions,
        headers: requestHeaders,
        body: body && !(body instanceof FormData) ? JSON.stringify(body) : body,
      });

      const data = await parseJson(res);

      if (!res.ok) {
        const error = new Error(getErrorMessage(data));
        error.status = res.status;
        error.data = data;
        throw error;
      }

      return unwrap ? normalizeBody(data) : data;
    } catch (err) {
      if (!shouldRetry(err, attempt, retryCount)) {
        throw err;
      }

      await sleep(retryDelayMs * (attempt + 1));
    }
  }

  throw new Error('Request failed');
}

export const apiClient = {
  request,
  get(path, options = {}) {
    return request(path, { method: 'GET', ...options });
  },
  post(path, body, options = {}) {
    return request(path, { method: 'POST', body, ...options });
  },
  put(path, body, options = {}) {
    return request(path, { method: 'PUT', body, ...options });
  },
  patch(path, body, options = {}) {
    return request(path, { method: 'PATCH', body, ...options });
  },
  delete(path, options = {}) {
    return request(path, { method: 'DELETE', ...options });
  },
  unwrap(data) {
    return normalizeBody(data);
  },
};
